// ========================
// CENTRALIZED CONSTANTS
// ========================
// All hardcoded values, strings, and configurations in one place

// ========================
// LLD STEP CONFIGURATION
// ========================
export const LLD_STEPS = [
  { 
    label: 'Features',
    key: 'features',
    dataFieldKey: 'features'
  },
  { 
    label: 'Diagram',
    key: 'diagram',
    dataFieldKey: 'diagram' // Combined entities and class diagram
  },
  { 
    label: 'Design Patterns',
    key: 'designPatterns',
    dataFieldKey: 'designPattern'
  },
  { 
    label: 'Code',
    key: 'code',
    dataFieldKey: 'codeFiles'
  }
];

// ========================
// HLD STEP CONFIGURATION
// ========================
export const HLD_STEPS = [
  { 
    label: 'Functional Requirements & Scale',
    key: 'functionalRequirementsAndScale',
    dataFieldKey: 'functionalRequirementsAndScale'
  },
  { 
    label: 'Non-Functional Requirements',
    key: 'nonFunctionalRequirements',
    dataFieldKey: 'nonFunctionalRequirements'
  },
  { 
    label: 'APIs',
    key: 'apis',
    dataFieldKey: 'apis'
  },
  { 
    label: 'HLD Diagram',
    key: 'hldDiagram',
    dataFieldKey: 'hldDiagram'
  },
  { 
    label: 'Deep Dives',
    key: 'deepDives',
    dataFieldKey: 'deepDives'
  }
];

// ========================
// UI TEXT CONSTANTS
// ========================
export const UI_TEXT = {
  // Titles
  TITLES: {
    FEATURES: "Features to Implement",
    DESIGN_PATTERNS: "Design Patterns",
    CLARIFICATIONS: "Ask questions to clarify requirements",
    FUNCTIONAL_REQS: "Functional Requirements",
    NON_FUNCTIONAL_REQS: "Non-Functional Requirements",
    SCALE_ESTIMATION: "Scale Estimation",
    HIGH_LEVEL_DESIGN: "High Level Design",
    DEEP_DIVE: "Deep Dive"
  },

  // Empty Messages
  EMPTY_MESSAGES: {
    FEATURES: "No features added yet",
    DESIGN_PATTERNS: "No design patterns selected yet",
    CLARIFICATIONS: "No clarifications yet",
    FUNCTIONAL_REQS: "No functional requirements defined yet",
    NON_FUNCTIONAL_REQS: "No non-functional requirements added yet",
    SCALE_ESTIMATION: "No scale estimation added yet",
    SCALE_ESTIMATIONS: "No scale considerations defined yet",
    GENERAL: "No items added yet"
  },

  // Placeholders
  PLACEHOLDERS: {
    FEATURES: "Write a feature or user story...",
    DESIGN_PATTERNS: "Select a design pattern...",
    DESIGN_PATTERN_USAGE: "Describe where and how you plan to use this pattern...",
    CLARIFICATIONS: "Ask a question to clarify requirements...",
    FUNCTIONAL_REQS: "Write a functional requirement...",
    NON_FUNCTIONAL_REQS: "Write a non-functional requirement...",
    SCALE_ESTIMATION: "Write scale estimation details...",
    GENERAL_ANSWER: "Type your answer here...",
    GENERAL_MESSAGE: "Type your message..."
  },

  // Button Labels
  BUTTONS: {
    ADD: "Add",
    EDIT: "Edit", 
    DELETE: "Delete",
    SAVE: "Save",
    CANCEL: "Cancel",
    SUBMIT: "Submit",
    SEND: "Send",
    HINT: "Get Hint",
    NEXT: "Next",
    PREVIOUS: "Previous",
    BACK: "Back",
    CREATE_PROBLEM: "Create Problem",
    ADD_PROBLEM: "Add Problem"
  },

  // Navigation
  NAVIGATION: {
    BACK_TO_PROBLEMS: "Back to Problems",
    LLD_DOCUMENT: "LLD Document",
    HLD_DOCUMENT: "HLD Document"
  },

  // Status Messages
  STATUS: {
    LOADING: "Loading...",
    SAVING: "Saving...",
    SAVED: "Saved",
    ERROR: "Error occurred",
    SUCCESS: "Success",
    NO_PROBLEMS_FOUND: "No problems found",
    PROBLEM_NOT_FOUND: "Problem not found"
  }
};

// ========================
// AI INTRODUCTION MESSAGES
// ========================
export const AI_INTRODUCTIONS = {
  // LLD Introductions
  LLD: {
    FEATURES: (problemTitle, contextualText = '') => 
      `Hi! Let's discuss features for ${problemTitle}. What are the main features users would want?`,

    DESIGN_PATTERNS: (problemTitle, contextualText = '') => 
      `Design Pattern Questions

Select the appropriate design pattern for each feature. Answer all questions and validate your responses.`
  },

  // HLD Introductions  
  HLD: {
    'Functional Reqs': (problemTitle, contextualText = '') =>
      `Hello! I'm here to help you define functional requirements for your "${problemTitle}" system.${contextualText}

Let's work together to:
1. Define core functionalities that address YOUR specific clarifications
2. Ensure requirements align with YOUR system's purpose
3. Create requirements that serve YOUR system's goals

Please define functional requirements that directly address your clarifications. I'll provide feedback based on YOUR specific system context.

What functional requirement would you like to start with?`,

    'Non-Functional Reqs': (problemTitle, contextualText = '') =>
      `Hello! I'm here to help you define non-functional requirements for your "${problemTitle}" system.${contextualText}

Let's work together to:
1. Define quality attributes that support YOUR functional requirements
2. Ensure non-functional requirements align with YOUR system's needs
3. Create requirements that enhance YOUR system's performance and reliability

Please define non-functional requirements that directly support your functional requirements. I'll provide feedback based on YOUR specific system context.

What non-functional requirement would you like to start with?`
  },

  // Clarification Panel
  CLARIFICATIONS: {
    INITIAL: (problemTitle, hasExistingContent = false) => {
      let introText = `Hi! I'm here to help you clarify any questions about "${problemTitle}".`;
      
      if (hasExistingContent) {
        introText += `\n\nI can see you've already started working on this problem. I'll consider your existing work when answering your questions to provide more relevant and contextual guidance.`;
      }
      
      introText += `\n\nWhat specific aspects of this problem would you like to understand better? Feel free to ask about:
- Requirements and constraints
- Technical details
- User expectations
- System boundaries
- Any other uncertainties

I'll help you get a clear understanding so we can design the best solution.`;
      
      return introText;
    }
  }
};

// ========================
// FIELD MAPPING
// ========================
export const FIELD_MAPPING = {
  'Features': 'features',
  'Entities': 'entities',
  'Design Patterns': 'designPattern',
  'Clarifications': 'clarifications',
  'Functional Reqs': 'functionalReqs',
  'Non-Functional Reqs': 'nonFunctionalReqs',
  'Scale': 'scale',
  'High Level Design': 'hld',
  'Deep Dive': 'deepDive'
};

// ========================
// STORAGE KEYS
// ========================
export const STORAGE_KEYS = {
  // Problem-specific keys (append problemId)
  LLD_ATTEMPT: (problemId) => `lld-attempt-${problemId}`,
  HLD_ATTEMPT: (problemId) => `hld-attempt-${problemId}`,
  LLD_STEP: (problemId) => `lld-step-${problemId}`,
  HLD_STEP: (problemId) => `hld-step-${problemId}`,
  LLD_CLASS_DIAGRAM: (problemId) => `lld-class-diagram-${problemId}`,
  LLD_EDITOR_FILES: (problemId) => `lld-editor-files-${problemId}`,
  LLD_LAST_RUN: (problemId) => `lld-last-run-${problemId}`,
  LLD_SIDEBAR_WIDTH: (problemId) => `lld-sidebar-width-${problemId}`,
  LLD_SIDEBAR_VISIBLE: (problemId) => `lld-sidebar-visible-${problemId}`,
  LLD_RIGHT_SIDEBAR_WIDTH: (problemId) => `lld-right-sidebar-width-${problemId}`,
  LLD_RIGHT_SIDEBAR_VISIBLE: (problemId) => `lld-right-sidebar-visible-${problemId}`,
  
  // AI Feedback keys
  AI_FEEDBACK: (componentType, problemId, stepName) => `ai-feedback-${componentType}-${problemId}-${stepName}`,
  
  // General keys
  USER_ID: 'userId',
  PROFILE_OBJECT: 'profileObject',
  CURRENT_LLD_PROBLEM_ID: 'current-lld-problemId',
  CURRENT_HLD_PROBLEM_ID: 'current-hld-problemId'
};

// ========================
// API ENDPOINTS
// ========================
export const API_ENDPOINTS = {
  LLD_PROBLEMS: '/lld-problems',
  LLD_ATTEMPT: '/lld-attempt',
  LLD_ATTEMPT_UPDATE_METADATA: '/lld-attempt/update-metadata',
  HLD_PROBLEMS: '/hld-problems', 
  HLD_ATTEMPT: '/hld-attempt',
  HLD_ATTEMPT_UPDATE_METADATA: '/hld-attempt/update-metadata',
  AI_FEEDBACK_SYNC: '/ai/sync-langchain',
  AI_ANSWERS_LLD_STEP: '/ai-answers/lld-step/all',
  AI_ANSWERS_HLD_STEP: '/ai-answers/hld-step/all'
};

// ========================
// DEFAULT VALUES
// ========================
export const DEFAULTS = {
  SIDEBAR_WIDTH: 350,
  RIGHT_SIDEBAR_WIDTH: 400,
  PAGINATION_LIMIT: 25,
  DEBOUNCE_DELAY: 1000,
  HINT_TIMEOUT: 50,
  USER_ID_FALLBACK: 'slazysloth_user',
  DIFFICULTY_LEVEL: 'Medium',
  
  // Default code files for LLD editor
  CODE_FILES: [
    {
      id: 'main-java',
      filename: 'Main.java',
      content: `public class Main {
    public static void main(String[] args) {
        System.out.println("Write your code here...");
    }
}`
    },
    {
      id: 'config-readme',
      filename: 'config/README.md',
      content: `# Config Folder

This folder contains application-level configurations, constants, and settings.

## Usage Examples:

### Database Configuration
\`\`\`java
// DatabaseConfig.java
public class DatabaseConfig {
    public static final String DB_URL = "jdbc:mysql://localhost:3306/mydb";
    public static final String DB_USER = "root";
    public static final String DB_PASSWORD = "password";
}
\`\`\`

### Application Constants
\`\`\`java
// AppConstants.java
public class AppConstants {
    public static final int MAX_RETRY_ATTEMPTS = 3;
    public static final String DEFAULT_TIMEZONE = "UTC";
    public static final int SESSION_TIMEOUT = 3600;
}
\`\`\`

### Environment Configuration
\`\`\`java
// EnvironmentConfig.java
public class EnvironmentConfig {
    public static final String ENV = System.getProperty("env", "development");
    public static final boolean DEBUG_MODE = "development".equals(ENV);
}
\`\`\`

## Best Practices:
- Keep all configuration values in one place
- Use constants for magic numbers and strings
- Separate environment-specific configs
- Document all configuration options`
    },
    {
      id: 'domain-readme',
      filename: 'domain/README.md',
      content: `# Domain Folder

This folder contains core business entities and domain models.

## Usage Examples:

### User Entity
\`\`\`java
// User.java
public class User {
    private String id;
    private String name;
    private String email;
    private String lastName;
    
    // Constructor, getters, setters
    public User(String name, String lastName) {
        this.id = UUID.randomUUID().toString();
        this.name = name;
        this.lastName = lastName;
    }
    
    // Business methods
    public String getFullName() {
        return name + " " + lastName;
    }
}
\`\`\`

### Book Entity
\`\`\`java
// Book.java
public class Book {
    private String id;
    private String name;
    private String author;
    private String isbn;
    
    // Constructor, getters, setters
    public Book(String name, String author) {
        this.id = UUID.randomUUID().toString();
        this.name = name;
        this.author = author;
    }
}
\`\`\`

## Best Practices:
- Keep entities focused on business logic
- Include validation logic in entities
- Use meaningful method names
- Avoid dependencies on external frameworks in domain objects`
    },
    {
      id: 'dto-readme',
      filename: 'dto/README.md',
      content: `# DTO Folder

This folder contains Data Transfer Objects for input/output operations.

## Usage Examples:

### Request DTOs
\`\`\`java
// CreateUserRequest.java
public class CreateUserRequest {
    private String name;
    private String lastName;
    private String email;
    
    // Constructor, getters, setters
    public CreateUserRequest(String name, String lastName, String email) {
        this.name = name;
        this.lastName = lastName;
        this.email = email;
    }
}
\`\`\`

### Response DTOs
\`\`\`java
// UserResponse.java
public class UserResponse {
    private String id;
    private String fullName;
    private String email;
    private String status;
    
    // Constructor, getters, setters
    public UserResponse(String id, String fullName, String email) {
        this.id = id;
        this.fullName = fullName;
        this.email = email;
        this.status = "ACTIVE";
    }
}
\`\`\`

### API Response Wrapper
\`\`\`java
// ApiResponse.java
public class ApiResponse<T> {
    private boolean success;
    private String message;
    private T data;
    
    // Constructor, getters, setters
    public static <T> ApiResponse<T> success(T data) {
        return new ApiResponse<>(true, "Success", data);
    }
}
\`\`\`

## Best Practices:
- Separate request and response DTOs
- Use DTOs for API boundaries
- Include validation annotations
- Keep DTOs simple and focused`
    },
    {
      id: 'repository-readme',
      filename: 'repository/README.md',
      content: `# Repository Folder

This folder contains data persistence layer and database operations.

## Usage Examples:

### User Repository
\`\`\`java
// UserRepository.java
public class UserRepository {
    private List<User> users = new ArrayList<>();
    
    public User save(User user) {
        users.add(user);
        return user;
    }
    
    public Optional<User> findById(String id) {
        return users.stream()
                   .filter(user -> user.getId().equals(id))
                   .findFirst();
    }
    
    public List<User> findAll() {
        return new ArrayList<>(users);
    }
    
    public void delete(String id) {
        users.removeIf(user -> user.getId().equals(id));
    }
}
\`\`\`

### Book Repository
\`\`\`java
// BookRepository.java
public class BookRepository {
    private List<Book> books = new ArrayList<>();
    
    public Book save(Book book) {
        books.add(book);
        return book;
    }
    
    public Optional<Book> findById(String id) {
        return books.stream()
                   .filter(book -> book.getId().equals(id))
                   .findFirst();
    }
    
    public List<Book> findByAuthor(String author) {
        return books.stream()
                   .filter(book -> book.getAuthor().equals(author))
                   .collect(Collectors.toList());
    }
}
\`\`\`

## Best Practices:
- Implement repository pattern
- Handle database exceptions gracefully
- Use transactions where needed
- Include proper error handling
- Consider using connection pooling`
    },
    {
      id: 'service-readme',
      filename: 'service/README.md',
      content: `# Service Folder

This folder contains business logic and service layer operations.

## Usage Examples:

### User Service
\`\`\`java
// UserService.java
public class UserService {
    private UserRepository userRepository;
    
    public UserService() {
        this.userRepository = new UserRepository();
    }
    
    public User createUser(String name, String lastName) {
        // Business logic validation
        if (name == null || name.trim().isEmpty()) {
            throw new IllegalArgumentException("Name cannot be empty");
        }
        
        User user = new User(name, lastName);
        return userRepository.save(user);
    }
    
    public User getUserById(String id) {
        return userRepository.findById(id)
                           .orElseThrow(() -> new RuntimeException("User not found"));
    }
    
    public List<User> getAllUsers() {
        return userRepository.findAll();
    }
}
\`\`\`

### Book Service
\`\`\`java
// BookService.java
public class BookService {
    private BookRepository bookRepository;
    
    public BookService() {
        this.bookRepository = new BookRepository();
    }
    
    public Book createBook(String name, String author) {
        // Business logic validation
        if (name == null || name.trim().isEmpty()) {
            throw new IllegalArgumentException("Book name cannot be empty");
        }
        
        Book book = new Book(name, author);
        return bookRepository.save(book);
    }
    
    public List<Book> getBooksByAuthor(String author) {
        return bookRepository.findByAuthor(author);
    }
}
\`\`\`

## Best Practices:
- Implement business logic in services
- Use dependency injection
- Handle exceptions appropriately
- Keep services focused and single-purpose
- Include proper validation`
    },
    {
      id: 'controller-readme',
      filename: 'controller/README.md',
      content: `# Controller Folder

This folder contains REST API endpoints and request handling.

## Usage Examples:

### User Controller
\`\`\`java
// UserController.java
public class UserController {
    private UserService userService;
    
    public UserController() {
        this.userService = new UserService();
    }
    
    // REST endpoint: POST /users
    public ApiResponse<UserResponse> createUser(CreateUserRequest request) {
        try {
            User user = userService.createUser(request.getName(), request.getLastName());
            UserResponse response = new UserResponse(user.getId(), user.getFullName(), request.getEmail());
            return ApiResponse.success(response);
        } catch (Exception e) {
            return new ApiResponse<>(false, e.getMessage(), null);
        }
    }
    
    // REST endpoint: GET /users/{id}
    public ApiResponse<UserResponse> getUser(String id) {
        try {
            User user = userService.getUserById(id);
            UserResponse response = new UserResponse(user.getId(), user.getFullName(), "");
            return ApiResponse.success(response);
        } catch (Exception e) {
            return new ApiResponse<>(false, e.getMessage(), null);
        }
    }
    
    // REST endpoint: GET /users
    public ApiResponse<List<UserResponse>> getAllUsers() {
        try {
            List<User> users = userService.getAllUsers();
            List<UserResponse> responses = users.stream()
                .map(user -> new UserResponse(user.getId(), user.getFullName(), ""))
                .collect(Collectors.toList());
            return ApiResponse.success(responses);
        } catch (Exception e) {
            return new ApiResponse<>(false, e.getMessage(), null);
        }
    }
}
\`\`\`

### Book Controller
\`\`\`java
// BookController.java
public class BookController {
    private BookService bookService;
    
    public BookController() {
        this.bookService = new BookService();
    }
    
    // REST endpoint: POST /books
    public ApiResponse<BookResponse> createBook(CreateBookRequest request) {
        try {
            Book book = bookService.createBook(request.getName(), request.getAuthor());
            BookResponse response = new BookResponse(book.getId(), book.getName(), book.getAuthor());
            return ApiResponse.success(response);
        } catch (Exception e) {
            return new ApiResponse<>(false, e.getMessage(), null);
        }
    }
}
\`\`\`

## Best Practices:
- Handle HTTP requests and responses
- Implement proper error handling
- Use appropriate HTTP status codes
- Validate input data
- Keep controllers thin - delegate to services
- Use consistent response formats`
    }
  ],
  
  // Default folders for LLD editor
  CODE_FOLDERS: ['config', 'domain', 'dto', 'repository', 'service', 'controller']
};

// ========================
// VALIDATION RULES
// ========================
export const VALIDATION = {
  MIN_SIDEBAR_WIDTH: 250,
  MAX_SIDEBAR_WIDTH: 600,
  MAX_RESPONSE_LENGTH: 80,
  MIN_RETRY_ATTEMPTS: 0,
  MAX_RETRY_ATTEMPTS: 2,
  RETRY_DELAY: 1000,
  SESSION_TIMEOUT: 60000 // 1 minute
};

// ========================
// STEP NAME MAPPINGS
// ========================
export const STEP_NAME_BY_NUMBER = {
  LLD: {
    0: 'Features',
    1: 'Diagram', 
    2: 'Design Patterns',
    3: 'Code'
  },
  HLD: {
    0: 'Functional Requirements & Scale',
    1: 'Non-Functional Requirements',
    2: 'APIs',
    3: 'HLD Diagram',
    4: 'Deep Dives'
  }
};

export default {
  LLD_STEPS,
  HLD_STEPS,
  UI_TEXT,
  AI_INTRODUCTIONS,
  FIELD_MAPPING,
  STORAGE_KEYS,
  API_ENDPOINTS,
  DEFAULTS,
  VALIDATION,
  STEP_NAME_BY_NUMBER
};
